import { FC, useEffect, useState } from 'react';
import NumberFlow from '@number-flow/react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Image, Input, QRCodeProps, Row, Space, Typography } from 'antd';
import {
    TbArrowRight,
    TbArrowUpRight,
    TbCircleCheck,
    TbCircleX,
    TbInfoCircle,
    TbLockPlus,
    TbRefresh,
} from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EnrichedAnchorTag } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores';
import { SetStateMethod } from '@/types';

interface EnableTFAProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    qrCode: string;
    setQrCode: SetStateMethod<string>;
    setEnabled: SetStateMethod<boolean>;
    setVerified: SetStateMethod<boolean>;
}

const EnableTFA: FC<EnableTFAProps> = ({ open, setOpen, setEnabled, qrCode, setQrCode, setVerified }): JSX.Element => {
    const { selectedUser: user, fetchUser, updateSelectedUserLocal } = useUserStore();
    const { token } = useAntDesignTheme();

    const [otp, setOtp] = useState<string>('');
    const [verifying, setVerifying] = useState(false);

    const verifyTFA = async () => {
        const normalizedOtp = otp.replace(/\D/g, '');

        if (verifying || normalizedOtp.length !== 6) {
            return;
        }

        try {
            setVerifying(true);

            const res = await authorizedAxiosInstance.post('/api/tfa/verify', { otp: normalizedOtp });

            if (res?.status === 200) {
                updateSelectedUserLocal({ otpEnabled: true });

                setEnabled(true);
                setVerified(true);
                setQrCode('');
                setOpen(false);

                toast('TFA Enabled', { icon: <TbLockPlus size='1rem' />, description: user?.emailAddress });

                await fetchUser();
            }
        } catch (error) {
            toast('Invalid OTP', { description: 'Please try again' });

            captureException(error);
        } finally {
            setVerifying(false);
        }
    };

    const [timeLeft, setTimeLeft] = useState(30 - ((Date.now() / 1000) % 30));

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeLeft(30 - ((Date.now() / 1000) % 30));
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const customStatusRender: QRCodeProps['statusRender'] = (info) => {
        switch (info.status) {
            case 'expired':
                return (
                    <div>
                        <TbCircleX style={{ color: token.red6 }} /> {info.locale?.expired}
                        <p>
                            <Button type='link' onClick={info.onRefresh}>
                                <TbRefresh /> {info.locale?.refresh}
                            </Button>
                        </p>
                    </div>
                );
            case 'loading':
                return (
                    <Space direction='vertical'>
                        <p>Loading...</p>
                    </Space>
                );
            case 'scanned':
                return (
                    <div>
                        <TbCircleCheck style={{ color: token.green6 }} /> {info.locale?.scanned}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PlatformAlert
                title='Protect your account'
                showIcon
                icon={<TbInfoCircle />}
                status='info'
                description={
                    <span>
                        To protect your account, Countercyclical recommends you add two-factor authentication (TFA) for
                        an extra layer of security. After enabling, you will be asked to input a code that is generated
                        from your smartphone or laptop when signing in. Learn more in{' '}
                        <a href='http://' target='_blank' rel='noopener noreferrer'>
                            our docs
                            <TbArrowUpRight style={{ verticalAlign: '-0.15rem', marginLeft: '0.125rem' }} />
                        </a>
                    </span>
                }
                style={{
                    marginBottom: '0.75rem',
                }}
            />
            <Divider style={{ marginBottom: '0.5rem' }} />
            <Row style={{ margin: '1rem 0' }}>
                <Col span={12}>
                    {/* <QRCode
                        type='svg'
                        errorLevel='H'
                        status={qrCode === '' ? 'loading' : 'active'}
                        value={qrCode}
                        color={token.colorText}
                        bgColor={token.colorBgContainer}
                        icon={AppIcon}
                        statusRender={customStatusRender}
                        size={160}
                    /> */}
                    {qrCode !== '' && <Image src={qrCode} />}
                </Col>
                <Col span={12}>
                    <Typography.Title level={5}>Setting up TFA on your Account</Typography.Title>
                    <ul style={{ margin: 0, padding: 0 }}>
                        <li>
                            Download & setup a two-factor authentication app like:{' '}
                            <ul>
                                <li>
                                    <EnrichedAnchorTag
                                        label={<>Apple Passwords</>}
                                        url='https://support.apple.com/guide/iphone/automatically-fill-in-verification-codes-ipha6173c19f/ios'
                                    />
                                </li>
                                <li>
                                    <a
                                        href='https://support.google.com/accounts/answer/1066447?hl=en&co=GENIE.Platform%3DAndroid'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        Google Authenticator
                                        <TbArrowUpRight style={{ verticalAlign: '-0.15rem', marginLeft: '0.125rem' }} />
                                    </a>
                                    <EnrichedAnchorTag
                                        label={<>Google Authenticator</>}
                                        url='https://support.google.com/accounts/answer/1066447?hl=en&co=GENIE.Platform%3DAndroid'
                                    />
                                </li>
                                <li>
                                    <a href='https://www.authy.com/' target='_blank' rel='noopener noreferrer'>
                                        Authy
                                        <TbArrowUpRight style={{ verticalAlign: '-0.15rem', marginLeft: '0.125rem' }} />
                                    </a>
                                </li>
                            </ul>
                        </li>
                        <li>Scan the barcode to the left</li>
                        <li>Input the code you're prompted with here & each time you login (while TFA is enabled)</li>
                    </ul>
                </Col>
            </Row>
            {timeLeft && (
                <PlatformAlert
                    title='Code expiration'
                    showIcon
                    icon={<TbInfoCircle />}
                    status='info'
                    description={
                        <span>
                            Six-digit OTP expires in{' '}
                            <strong>
                                <NumberFlow value={Math.ceil(timeLeft)} />
                            </strong>{' '}
                            seconds
                        </span>
                    }
                    style={{
                        marginBottom: '0.75rem',
                        marginTop: '0.5rem',
                    }}
                />
            )}
            <Divider style={{ marginTop: '1rem', marginBottom: '0.5rem' }} />
            <Form layout='vertical'>
                <Row gutter={[16, 0]}>
                    <Col flex='auto'>
                        <Form.Item label='One-Time Passcode (OTP)' style={{ marginBottom: 0 }}>
                            <Input
                                maxLength={6}
                                placeholder='Input Six-Digit Code...'
                                style={{ width: '100%' }}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                            />
                        </Form.Item>
                    </Col>
                    <Col flex='none'>
                        <Form.Item label={<></>} style={{ marginBottom: 0 }}>
                            <Button type='primary' onClick={verifyTFA} loading={verifying} disabled={otp.length !== 6}>
                                Verify & Enable
                                <TbArrowRight
                                    size='1rem'
                                    style={{ verticalAlign: '-0.175rem', marginLeft: '0.375rem' }}
                                />
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </>
    );
};

export default EnableTFA;
